import React, {useContext, useEffect, useMemo, useRef, useState, useCallback} from 'react';
import { Form, Button } from 'react-bootstrap';
import ThemedSelect from '../shared/ThemedSelect';
import {useTradeJournals} from "../../hooks/useTradeJournals";
import { AuthContext } from "../../contexts/AuthContext";
import { AppStateContext } from "../../contexts/AppStateContext";
import { TradingContext } from "../../contexts/TradingContext";
import { colors } from '../../theme/colors';
import analyticsService from '../../services/analyticsService';
import {
  buildJournalSavePayload,
  DEFAULT_JOURNAL_DATETIME_KEY,
  DEFAULT_JOURNAL_TICKER_KEY,
  findJournalDatetimeFieldKey,
  findJournalTickerFieldKey,
  formatJournalDatetimeLocal,
  resolveJournalTradeOpenAt,
} from './journalListUtils';
import JournalTemplateBuilder from './JournalTemplateBuilder';
import { resolveClosedPositionPnl } from '../../utils/closedPositionPnl';

const TradeJournalForm = ({
  postSubmitCallback,
  initialJournal = null,
  positionData = null,
  isAutoOpened = false,
  tradeId = null,
  tradeSymbol = null,
  tradeOpenAt = null,
  templateEditMode = false,
  onTemplateReady = null,
  onEditTemplate = null,
}) => {
  const { createTradeJournal, updateTradeJournal, getTradeJournalTemplate } = useTradeJournals();
  const [template, setTemplate] = useState({ fields: [] });
  const [templateName, setTemplateName] = useState('Default');
  const [templateLoaded, setTemplateLoaded] = useState(!!initialJournal);
  const [values, setValues] = useState({});
  const onTemplateReadyRef = useRef(onTemplateReady);
  onTemplateReadyRef.current = onTemplateReady;
  const editSeedRef = useRef(null);
  const wasEditingRef = useRef(false);
  if (templateEditMode && !wasEditingRef.current) {
    editSeedRef.current = { ...template, name: template.name || templateName };
  }
  if (!templateEditMode) {
    editSeedRef.current = null;
  }
  wasEditingRef.current = !!templateEditMode;
  const { user } = useContext(AuthContext);
  const { activeChartTicker } = useContext(AppStateContext);
  const { selectedAccount } = useContext(TradingContext);
  const loadedRef = useRef(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Track window width for responsive padding
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sortedFields = useMemo(() => {
    if (!Array.isArray(template.fields)) return [];
    return template.fields
      .map((f, i) => ({ ...f, __i: i }))
      .sort((a, b) => ((a.position ?? a.__i) - (b.position ?? b.__i)));
  }, [template]);

  // Helper to check if a field is required (ticker or datetime)
  const isRequiredField = useCallback((field) => {
    const fieldKey = (field.key || field.label || '').toLowerCase();
    const fieldType = field.type || '';
    
    // Ticker/symbol fields are always required
    const isTickerField = fieldKey.includes('ticker') || fieldKey.includes('symbol');
    
    // Datetime fields are always required
    const isDateTimeField = fieldType === 'datetime' || fieldKey.includes('datetime') || 
                           (fieldKey.includes('date') && fieldKey.includes('time'));
    
    return isTickerField || isDateTimeField;
  }, []);

  const handleTemplateEdited = useCallback((nextTemplate, name) => {
    const nextName = name || nextTemplate?.name || 'Default';
    setTemplateName(nextName);
    setTemplate({ ...(nextTemplate || { fields: [] }), name: nextName });
  }, []);

  const markTemplateReady = useCallback(() => {
    setTemplateLoaded(true);
    onTemplateReadyRef.current?.(true);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (loadedRef.current) {
        markTemplateReady();
        return;
      }
      if (initialJournal && initialJournal.entry) {
        loadedRef.current = true;
        const entryTemplate = initialJournal.entry.template || { fields: [] };
        setTemplate(entryTemplate);
        setTemplateName(entryTemplate.name || 'Default');
        setValues(initialJournal.entry.values || {});
        markTemplateReady();
        return;
      }
      if (!user?.token) return;
      try {
      const tmpl = await getTradeJournalTemplate();
      if (tmpl && tmpl.template) {
        loadedRef.current = true;
        const loadedName = tmpl.name || tmpl.template.name || 'Default';
        setTemplateName(loadedName);
        setTemplate({ ...tmpl.template, name: loadedName });
        
        // Pre-populate values for new journal
        const prePopulatedValues = {};
        const openAt = tradeOpenAt || resolveJournalTradeOpenAt(positionData, null);
        const datetimeLocal = formatJournalDatetimeLocal(openAt);
        
        // MINIMUM REQUIREMENT: Auto-fill ticker for ALL journals
        // Read activeChartTicker fresh from context inside useEffect to ensure we get current value
        const currentActiveChartTicker = activeChartTicker;
        
        // When manually opening (!isAutoOpened): Always use context activeChartTicker directly (most current)
        // When auto-opened from position close: Use position Symbol
        // Normalize empty strings to null for consistent checking
        const normalizeTicker = (ticker) => (ticker && String(ticker).trim()) || null;
        let tickerToUse = '';
        
        if (isAutoOpened) {
          tickerToUse = normalizeTicker(tradeSymbol) || '';
        } else {
          // Manually opened - always use context activeChartTicker directly (read fresh from AppStateContext)
          // This ensures we always get the current active chart ticker
          tickerToUse = normalizeTicker(currentActiveChartTicker) || '';
        }
        
        // Auto-fill template fields with ticker and datetime
        if (tmpl.template && Array.isArray(tmpl.template.fields)) {
          tmpl.template.fields.forEach(field => {
            const fieldKey = field.key || field.label;
            const fieldKeyLower = fieldKey.toLowerCase();
            
            // Auto-fill ticker fields
            const isTickerField = fieldKeyLower.includes('ticker') || fieldKeyLower.includes('symbol');
            if (field.type === 'text' && isTickerField) {
              // Use tickerToUse if available, otherwise fall back to context activeChartTicker (normalized)
              const normalizeTickerForField = (ticker) => (ticker && String(ticker).trim()) || null;
              const finalTicker = tickerToUse || normalizeTickerForField(currentActiveChartTicker) || '';
              if (finalTicker) {
                prePopulatedValues[fieldKey] = finalTicker.toUpperCase();
              }
            }
            
            // Auto-fill datetime fields (only for datetime type fields in template)
            if (field.type === 'datetime') {
              prePopulatedValues[fieldKey] = datetimeLocal;
            }
            
            // Auto-fill other fields marked with autoFill: true
            if (field.autoFill && !isTickerField && field.type !== 'datetime' && positionData) {
              if (field.type === 'text') {
                // Additional auto-fill logic for other text fields if needed
              }
            }
          });
        }
        
        // Auto-fill position-specific data if available (only for fields that match template field keys)
        if (positionData && tmpl.template && Array.isArray(tmpl.template.fields)) {
          tmpl.template.fields.forEach(field => {
            const fieldKey = field.key || field.label;
            const fieldKeyLower = fieldKey.toLowerCase();
            
            // Auto-fill quantity/shares fields
            if (positionData.Quantity && (fieldKeyLower.includes('quantity') || fieldKeyLower.includes('shares'))) {
              prePopulatedValues[fieldKey] = Math.abs(parseFloat(positionData.Quantity));
            }
            
            // Auto-fill P&L fields from the position we just closed (or still hold).
            const closedPnl = resolveClosedPositionPnl(positionData, positionData);
            if (closedPnl != null && (fieldKeyLower.includes('pnl') || fieldKeyLower.includes('profit') || fieldKeyLower.includes('loss'))) {
              prePopulatedValues[fieldKey] = closedPnl;
            }
            
            // Auto-fill entry/average price fields (but not fields like "Why did you enter?")
            // Only fill fields that are specifically about entry price, not fields that just contain "enter"
            if (positionData.AveragePrice && 
                (fieldKeyLower.includes('entry price') || 
                 fieldKeyLower.includes('entryprice') ||
                 (fieldKeyLower.includes('average') && fieldKeyLower.includes('price')) ||
                 (fieldKeyLower === 'entry' && field.type === 'currency'))) {
              prePopulatedValues[fieldKey] = parseFloat(positionData.AveragePrice);
            }
          });
        }
        
        const { valuesToSave: initialValues } = buildJournalSavePayload({
          values: prePopulatedValues,
          fields: tmpl.template.fields,
          tradeSymbol: tickerToUse || tradeSymbol,
          tradeOpenAt: openAt,
        });
        setValues(initialValues);
      }
      } finally {
        markTemplateReady();
      }
    };
    load();
  }, [user?.token, initialJournal, positionData, activeChartTicker, isAutoOpened, getTradeJournalTemplate, tradeSymbol, tradeOpenAt, markTemplateReady]);

  // Keep ticker field in sync when modal header symbol resolves (e.g. from connected trade).
  useEffect(() => {
    if (!tradeSymbol) return;
    const tickerFieldKey = findJournalTickerFieldKey(sortedFields) || DEFAULT_JOURNAL_TICKER_KEY;
    if (!tickerFieldKey) return;
    setValues((prev) => {
      if (prev[tickerFieldKey] && String(prev[tickerFieldKey]).trim()) return prev;
      return { ...prev, [tickerFieldKey]: tradeSymbol };
    });
  }, [tradeSymbol, sortedFields]);

  // Keep trade datetime in sync when connected trade / position open time resolves.
  useEffect(() => {
    if (!tradeOpenAt) return;
    const datetimeFieldKey = findJournalDatetimeFieldKey(sortedFields) || DEFAULT_JOURNAL_DATETIME_KEY;
    if (!datetimeFieldKey) return;
    const datetimeLocal = formatJournalDatetimeLocal(tradeOpenAt);
    if (!datetimeLocal) return;
    setValues((prev) => {
      if (prev[datetimeFieldKey] && String(prev[datetimeFieldKey]).trim()) return prev;
      return { ...prev, [datetimeFieldKey]: datetimeLocal };
    });
  }, [tradeOpenAt, sortedFields]);

  const onChange = (fieldKey, value) => {
    // Auto-capitalize ticker fields
    const fieldKeyLower = fieldKey.toLowerCase();
    const isTickerField = fieldKeyLower.includes('ticker') || fieldKeyLower.includes('symbol');
    
    if (isTickerField && typeof value === 'string') {
      value = value.toUpperCase();
    }
    
    setValues(prev => ({ ...prev, [fieldKey]: value }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      valuesToSave,
      ticker,
      datetime,
      positionTimestamp,
    } = buildJournalSavePayload({
      values,
      fields: sortedFields,
      tradeSymbol,
      tradeOpenAt: tradeOpenAt || resolveJournalTradeOpenAt(positionData, null),
    });

    if (!ticker || ticker.trim() === '') {
      alert('Ticker is required for all trade journals. Please enter a ticker symbol.');
      return;
    }

    if (!datetime) {
      alert('Trade date/time is required for all trade journals. Please enter a date and time.');
      return;
    }
    
    // Always use selected account from context (or account from positionData if auto-opened)
    const accountIdToUse = selectedAccount?.AccountID || positionData?.AccountID || null;
    
    if (!accountIdToUse) {
      console.error('[TradeJournalForm] No account ID available!', { selectedAccount, positionData });
      alert('Unable to determine account. Please ensure an account is selected.');
      return;
    }
    
    console.log('[TradeJournalForm] Creating journal with account_id:', accountIdToUse);

    const entry = {
      template: template,
      values: valuesToSave,
      createdAt: initialJournal?.entry?.createdAt || new Date().toISOString(),
      position_timestamp: positionTimestamp,
    };
    // Include account_id for journal compliance tracking - use selected account automatically
    const res = initialJournal?.id 
      ? await updateTradeJournal(initialJournal.id, entry, accountIdToUse) 
      : await createTradeJournal(entry, accountIdToUse, tradeId || null);
    if (res && !res.error) {
      // Track journal entry created
      analyticsService.trackTradingEvent('journal_entry_created', {
        positionData: positionData,
        autoOpened: false,
        isEdit: !!initialJournal?.id,
        ticker: ticker
      });

      // Cache refresh + event are handled in useTradeJournals after successful save
      postSubmitCallback();
    }
  };

  // Show setup prompt when no fields are configured and this is a new journal (not editing)
  const showSetupPrompt = templateLoaded && sortedFields.length === 0 && !initialJournal;

  // Responsive padding based on screen width
  // Mobile (< 768px): 12px, Tablet (768-1024px): 20px, Desktop (> 1024px): 24px
  const getHorizontalPadding = () => {
    if (windowWidth < 768) return '12px'; // Mobile
    if (windowWidth < 1024) return '20px'; // Tablet
    return '24px'; // Desktop
  };

  // Common form field styles for compact design
  const formFieldStyle = {
    backgroundColor: colors.bgInput,
    borderColor: colors.border,
    color: colors.text,
    fontSize: '0.9rem',
    padding: `6px ${getHorizontalPadding()}`,
    borderRadius: '4px'
  };

  const labelStyle = {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: colors.textMuted,
    marginBottom: '4px'
  };

  if (templateEditMode && !initialJournal) {
    return (
      <div style={{ padding: 0 }}>
        <p className="journal-template-edit-hint">
          Add, rename, or reorder prompts. Changes save automatically. Click Done to fill out this entry.
        </p>
        {editSeedRef.current && (
          <JournalTemplateBuilder
            seedTemplate={editSeedRef.current}
            hideSelector
            autoSave
            autoLoadSaved={false}
            onTemplateChange={handleTemplateEdited}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: 0 }}>
      {showSetupPrompt && (
        <div style={{ 
          backgroundColor: colors.bgCard, 
          border: `1px solid ${colors.border}`, 
          borderRadius: '6px', 
          padding: '12px', 
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          <p style={{ color: colors.text, fontSize: '0.9rem', marginBottom: '4px', fontWeight: 500 }}>
            No journal prompts configured yet
          </p>
          <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: '12px' }}>
            Add the questions you want to answer for each trade.
          </p>
          <button
            type="button"
            onClick={() => onEditTemplate?.()}
            style={{
              color: colors.primary,
              textDecoration: 'none',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
            }}
          >
            Edit template
          </button>
        </div>
      )}
      <Form onSubmit={handleSubmit} style={{ margin: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {sortedFields.map((f) => {
            const key = f.key || f.label;
            const type = f.type || 'text';
            const isRequired = isRequiredField(f);
            
            if (type === 'text') {
              return (
                <div key={key} style={{ marginBottom: 0 }}>
                  <label style={labelStyle}>
                    {f.label}
                    {isRequired && <span style={{ color: colors.danger || '#dc3545', marginLeft: '4px' }}>*</span>}
                  </label>
                  <Form.Control 
                    type="text" 
                    value={values[key] || ''} 
                    onChange={(e) => onChange(key, e.target.value)} 
                    style={formFieldStyle}
                    size="sm"
                    required={isRequired}
                  />
                </div>
              );
            }
            if (type === 'datetime') {
              return (
                <div key={key} style={{ marginBottom: 0 }}>
                  <label style={labelStyle}>
                    {f.label}
                    {isRequired && <span style={{ color: colors.danger || '#dc3545', marginLeft: '4px' }}>*</span>}
                  </label>
                  <Form.Control 
                    type="datetime-local" 
                    value={values[key] || ''} 
                    onChange={(e) => onChange(key, e.target.value)} 
                    style={formFieldStyle}
                    size="sm"
                    required={isRequired}
                  />
                </div>
              );
            }
            if (type === 'rank') {
              const max = Number(f.max || 5);
              const rankOptions = [
                { value: '', label: 'Select' },
                ...Array.from({ length: max }, (_, i) => i + 1).map((v) => ({
                  value: String(v),
                  label: String(v),
                })),
              ];
              return (
                <div key={key} style={{ marginBottom: 0 }}>
                  <label style={labelStyle}>{f.label}</label>
                  <ThemedSelect
                    value={values[key] === undefined || values[key] === null || values[key] === '' ? '' : String(values[key])}
                    options={rankOptions}
                    onChange={(val) => onChange(key, val === '' ? '' : val)}
                    width="100%"
                  />
                </div>
              );
            }
            if (type === 'single') {
              const rawOptions = Array.isArray(f.options) ? f.options : [];
              const selectOptions = [
                { value: '', label: 'Select' },
                ...rawOptions.map((opt) => ({
                  value: String(opt.value ?? opt),
                  label: String(opt.label ?? opt),
                })),
              ];
              return (
                <div key={key} style={{ marginBottom: 0 }}>
                  <label style={labelStyle}>{f.label}</label>
                  <ThemedSelect
                    value={values[key] === undefined || values[key] === null ? '' : String(values[key])}
                    options={selectOptions}
                    onChange={(val) => onChange(key, val)}
                    width="100%"
                  />
                </div>
              );
            }
            if (type === 'checkbox') {
              const checked = Boolean(values[key]);
              return (
                <div key={key} style={{ marginBottom: 0 }}>
                  <Form.Check
                    type="checkbox"
                    label={<span style={{ fontSize: '0.85rem', color: colors.text }}>{f.label}</span>}
                    checked={checked}
                    onChange={(e) => onChange(key, e.target.checked)}
                    style={{ marginTop: '8px' }}
                  />
                </div>
              );
            }
            if (type === 'number') {
              return (
                <div key={key} style={{ marginBottom: 0 }}>
                  <label style={labelStyle}>{f.label}</label>
                  <Form.Control 
                    type="number" 
                    value={values[key] ?? ''} 
                    onChange={(e) => onChange(key, e.target.value === '' ? '' : Number(e.target.value))} 
                    style={formFieldStyle}
                    size="sm"
                  />
                </div>
              );
            }
            if (type === 'currency') {
              return (
                <div key={key} style={{ marginBottom: 0 }}>
                  <label style={labelStyle}>{f.label}</label>
                  <Form.Control 
                    type="number" 
                    step="0.01" 
                    value={values[key] ?? ''} 
                    onChange={(e) => onChange(key, e.target.value === '' ? '' : Number(e.target.value))} 
                    style={formFieldStyle}
                    size="sm"
                  />
                </div>
              );
            }
            if (type === 'multi') {
              const options = Array.isArray(f.options) ? f.options : [];
              const selectedValues = Array.isArray(values[key]) ? values[key] : (values[key] ? [values[key]] : []);
              
              return (
                <div key={key} style={{ marginBottom: 0 }}>
                  <label style={labelStyle}>{f.label}</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                    {options.map(opt => {
                      const optValue = opt.value || opt;
                      const optLabel = opt.label || opt;
                      const isChecked = selectedValues.includes(optValue);
                      
                      return (
                        <Form.Check
                          key={optValue}
                          type="checkbox"
                          id={`${key}-${optValue}`}
                          label={<span style={{ fontSize: '0.85rem', color: colors.text }}>{optLabel}</span>}
                          checked={isChecked}
                          onChange={(e) => {
                            const newSelected = e.target.checked
                              ? [...selectedValues, optValue]
                              : selectedValues.filter(v => v !== optValue);
                            onChange(key, newSelected);
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
        {!showSetupPrompt && (
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: `1px solid ${colors.border}`
          }}>
            <Button 
              variant="primary" 
              type="submit"
              style={{
                fontSize: '0.9rem',
                padding: '6px 20px',
                fontWeight: 500
              }}
            >
              {initialJournal ? 'Update' : 'Save'}
            </Button>
            <Button 
              variant="outline-secondary" 
              type="button"
              onClick={postSubmitCallback}
              style={{
                fontSize: '0.9rem',
                padding: '6px 20px',
                borderColor: colors.border,
                color: colors.textMuted
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default TradeJournalForm;
